import { CommonModule } from '@angular/common';
import { Component, inject, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MenuItem, MessageService } from 'primeng/api';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { MessageModule } from 'primeng/message';
import { TableModule } from 'primeng/table';
import { TabsModule } from 'primeng/tabs';
import { InputTextModule } from 'primeng/inputtext';
import { TooltipModule } from 'primeng/tooltip';
import { PdfAdminService, PdfChapter, PdfChunk, PdfDocument, PdfDocumentUsage, PdfUploadProgress } from '../../services/pdf-admin.service';

@Component({
  imports: [
    CommonModule,
    FormsModule,
    BreadcrumbModule,
    ButtonModule,
    TableModule,
    DialogModule,
    MessageModule,
    TabsModule,
    InputTextModule,
    TooltipModule
  ],
  templateUrl: './pdf-dokumente.html',
  styleUrl: './pdf-dokumente.scss'
})
export class PdfDokumente implements OnInit {
  private pdfAdminService = inject(PdfAdminService);
  private messageService = inject(MessageService);

  protected home: MenuItem = { label: 'PDF-Dokumente' };

  protected isUploading = false;
  protected uploadProgressPercent = 0;
  protected uploadProgressMessage = '';
  protected selectedFile: File | null = null;
  protected documents: PdfDocument[] = [];

  protected chaptersDialogVisible = false;
  protected selectedDocumentName = '';
  protected selectedDocumentChapters: PdfChapter[] = [];
  protected selectedDocumentChunks: PdfChunk[] = [];
  protected selectedDocumentUsages: PdfDocumentUsage[] = [];
  protected loadingChapters = false;
  protected editingDocumentId: string | null = null;
  protected editingNameForUser = '';
  protected isSavingDocumentName = false;

  public async ngOnInit() {
    await this.loadDocuments();
  }

  protected onFileChange(event: Event) {
    const input = event.target as HTMLInputElement;
    this.selectedFile = input.files?.[0] ?? null;
  }

  protected async uploadPdf() {
    if (!this.selectedFile) {
      this.messageService.add({ severity: 'warn', summary: 'Hinweis', detail: 'Bitte eine PDF-Datei auswählen.' });
      return;
    }

    this.isUploading = true;
    this.uploadProgressPercent = 0;
    this.uploadProgressMessage = 'Upload startet';
    try {
      const result = await this.pdfAdminService.uploadPdf(this.selectedFile, (progress: PdfUploadProgress) => {
        this.uploadProgressPercent = progress.percent;
        this.uploadProgressMessage = progress.message;
      });
      this.messageService.add({
        severity: 'success',
        summary: 'Upload erfolgreich',
        detail: `${this.selectedFile.name} verarbeitet (${result.totalPages} Seiten, ${result.chunks} Chunks).`
      });
      this.selectedFile = null;
      await this.loadDocuments();
    } catch (error) {
      this.messageService.add({ severity: 'error', summary: 'Fehler', detail: 'PDF-Upload fehlgeschlagen.' });
    } finally {
      this.isUploading = false;
      if (this.uploadProgressPercent < 100) {
        this.uploadProgressPercent = 0;
      }
      this.uploadProgressMessage = '';
    }
  }

  protected async deleteDocument(doc: PdfDocument) {
    try {
      await this.pdfAdminService.deletePdf(doc._id);
      this.messageService.add({ severity: 'success', summary: 'Gelöscht', detail: `${doc.name} wurde gelöscht.` });
      await this.loadDocuments();
    } catch {
      this.messageService.add({ severity: 'error', summary: 'Fehler', detail: 'Dokument konnte nicht gelöscht werden.' });
    }
  }

  protected async showDetails(doc: PdfDocument) {
    this.selectedDocumentName = doc.nameForUser || doc.name;
    this.loadingChapters = true;
    this.chaptersDialogVisible = true;
    try {
      const [chapters, chunks, usages] = await Promise.all([
        this.pdfAdminService.getChapters(doc._id),
        this.pdfAdminService.getChunks(doc._id),
        this.pdfAdminService.getDocumentUsages(doc._id)
      ]);
      this.selectedDocumentChapters = chapters;
      this.selectedDocumentChunks = chunks;
      this.selectedDocumentUsages = usages;
    } catch {
      this.selectedDocumentChapters = [];
      this.selectedDocumentChunks = [];
      this.selectedDocumentUsages = [];
      this.messageService.add({ severity: 'error', summary: 'Fehler', detail: 'Dokumentdetails konnten nicht geladen werden.' });
    } finally {
      this.loadingChapters = false;
    }
  }

  protected startEditingDocumentName(doc: PdfDocument): void {
    this.editingDocumentId = doc._id;
    this.editingNameForUser = doc.nameForUser || doc.name;
  }

  protected cancelEditingDocument(): void {
    this.editingDocumentId = null;
    this.editingNameForUser = '';
  }

  protected async saveDocumentName(doc: PdfDocument): Promise<void> {
    if (!this.editingNameForUser.trim()) {
      this.messageService.add({ severity: 'warn', summary: 'Hinweis', detail: 'Bitte einen Namen eingeben.' });
      return;
    }

    this.isSavingDocumentName = true;
    try {
      const result = await this.pdfAdminService.updatePdfDocumentName(doc._id, this.editingNameForUser.trim());
      this.messageService.add({
        severity: 'success',
        summary: 'Erfolgreich',
        detail: 'Dokumentname aktualisiert'
      });
      // Update the document in the list
      doc.nameForUser = this.editingNameForUser.trim();
      this.editingDocumentId = null;
      this.editingNameForUser = '';
      // Update selected document name if it's the one being edited
      if (this.selectedDocumentName === doc.name) {
        this.selectedDocumentName = doc.nameForUser;
      }
    } catch (error) {
      this.messageService.add({
        severity: 'error',
        summary: 'Fehler',
        detail: 'Dokumentname konnte nicht aktualisiert werden'
      });
      console.error('Error updating document name:', error);
    } finally {
      this.isSavingDocumentName = false;
    }
  }

  private async loadDocuments() {
    this.documents = await this.pdfAdminService.listPdfs();
  }
}
